import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import clsx from "clsx";
import { AlarmClockOff, AlertTriangle, Building2, CheckCircle2, CheckSquare, Square, ChevronRight, ClipboardCheck, ClipboardX, ExternalLink, Send, UserX, Wrench, FolderKanban, GitBranch, Hourglass, Scale, Search, Siren, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, post } from "../lib/api";
import { Card, Empty, ErrorNote, fmtDate, PageHeader, Stat } from "../components/ui";
import { Due, PriorityBadge, StatusPill } from "../components/work";

const eur = (n: unknown) => (n === null || n === undefined ? "" : `€${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`);
type Row = { id: string; ref: string; title: string; priority?: string; dueAt?: string | null; state?: string; slaState?: string };
const ItemList = ({ items, empty }: { items: Row[]; empty: string }) =>
  items.length ? (
    <ul className="space-y-1 text-sm">{items.map((i) => <li key={i.id} className="flex flex-wrap items-center gap-2"><Link to="/work/$id" params={{ id: i.id }} className="hover:underline">{i.title}</Link><span className="muted text-xs">{i.ref}</span>{i.priority && <PriorityBadge p={i.priority} />}{i.dueAt !== undefined && <Due at={i.dueAt ?? null} />}</li>)}</ul>
  ) : <p className="muted text-sm">{empty}</p>;

// ───────────────────────── workspaces ─────────────────────────
export function WorkspacesPage() {
  const ws = useQuery({ queryKey: ["workspaces"], queryFn: () => api<{ items: Array<{ id: string; code: string; name: string; company: string }> }>("/api/workspaces") });
  return (
    <div>
      <PageHeader title="Workspaces" subtitle="One page per business: what's open, late, failed or waiting." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {ws.data?.items.map((w) => (
          <Link key={w.id} to="/workspaces/$code" params={{ code: w.code }} className="panel group flex items-center gap-4 p-5 transition hover:-translate-y-px hover:border-brand-200 hover:shadow-md">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100"><Building2 size={22} /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-base font-bold group-hover:text-brand-700">{w.name}</span>
              <span className="muted block truncate text-sm">{w.company}</span>
            </span>
            <ChevronRight size={18} className="subtle transition group-hover:translate-x-0.5 group-hover:text-brand-600" />
          </Link>
        ))}
      </div>
    </div>
  );
}

interface WS {
  workspace: { name: string; code: string };
  counts: { open: number; overdue: number; slaBreached: number; awaitingSignOff: number; doneToday: number };
  faults: Row[];
  breached: Row[];
  inspections: Array<{ id: string; ref: string; title: string; status: string; score: number | null; failedCount: number; criticalFailed: number; dueAt: string | null }>;
  approvals: Array<{ id: string; ref: string; title: string; amount: string | null }>;
  external: Array<{ system: string; open: number }>;
  requests: Array<{ name: string; n: number }>;
  projects: Array<{ id: string; ref: string; name: string; health: string; status: string }>;
  unassigned: Row[];
}

export function WorkspacePage() {
  const { code } = useParams({ strict: false }) as { code: string };
  const q = useQuery({ queryKey: ["workspace", code], queryFn: () => api<WS>(`/api/workspaces/${code}`), refetchInterval: 60_000 });
  if (!q.data) return q.isLoading ? <Empty>Loading…</Empty> : <ErrorNote error={q.error} />;
  const d = q.data;
  const item = (i: Row) => ({ key: i.id, to: `/work/${i.id}`, title: i.title, sub: i.ref, right: <span className="flex shrink-0 items-center gap-2">{i.priority && <PriorityBadge p={i.priority} />}{i.dueAt !== undefined && <Due at={i.dueAt ?? null} />}</span> });
  return (
    <div className="space-y-5">
      <PageHeader eyebrow={new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} title={`${d.workspace.name} today`} subtitle="What's open, late, failed or waiting in this business." />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Stat label="Open" value={d.counts.open} />
        <Stat label="Overdue" value={d.counts.overdue} tone={d.counts.overdue ? "bad" : "default"} />
        <Stat label="SLA missed" value={d.counts.slaBreached} tone={d.counts.slaBreached ? "bad" : "default"} />
        <Stat label="Awaiting sign-off" value={d.counts.awaitingSignOff} tone={d.counts.awaitingSignOff ? "warn" : "default"} />
        <Stat label="Done (24h)" value={d.counts.doneToday} tone={d.counts.doneToday ? "good" : "default"} />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <PulseList title="Today's inspections" icon={<ClipboardCheck size={16} />} empty="No inspections scheduled or done today." rows={d.inspections.map((r) => ({
          key: r.id, to: `/inspections/${r.id}`, title: r.title, sub: r.ref,
          right: <span className="flex shrink-0 items-center gap-2"><StatusPill state={r.status === "submitted" ? "done" : r.status === "in_progress" ? "in_progress" : "open"} label={r.status.replace("_", " ")} />{r.score !== null && <span className={clsx("text-xs font-bold num", r.criticalFailed ? "text-red-600" : r.failedCount ? "text-amber-600" : "text-emerald-600")}>{Number(r.score)}%</span>}</span>,
        }))} />
        <PulseList title="Open faults" icon={<Wrench size={16} />} empty="No open faults." rows={d.faults.map(item)} />
        <PulseList title="Missed SLA" icon={<AlarmClockOff size={16} />} empty="Nothing late against SLA." rows={d.breached.map(item)} />
        <PulseList title="Nobody has picked up" icon={<UserX size={16} />} empty="Everything has an owner." rows={d.unassigned.map(item)} />
        <PulseList title="Approvals waiting" icon={<Scale size={16} />} empty="None." rows={d.approvals.map((a) => ({ key: a.id, to: `/approvals/${a.id}`, title: a.title, sub: a.ref, right: <span className="font-bold num">{eur(a.amount)}</span> }))} />
        <PulseList title="Requests this week" icon={<Send size={16} />} empty="None." rows={d.requests.map((r) => ({ key: r.name, title: r.name, right: <span className="font-bold num">{r.n}</span> }))} />
        {d.projects.length > 0 && <PulseList title="Projects" icon={<FolderKanban size={16} />} rows={d.projects.map((p) => ({ key: p.id, to: `/projects/${p.id}`, title: p.name, sub: p.ref, dot: p.health === "red" ? "bg-red-500" : p.health === "amber" ? "bg-amber-500" : "bg-emerald-500" }))} />}
        {d.external.length > 0 && <PulseList title="In other apps" icon={<ExternalLink size={16} />} rows={d.external.map((e) => ({ key: e.system, title: e.system, right: <span className="font-bold num">{e.open} open</span> }))} />}
      </div>
    </div>
  );
}

// ───────────────────────── Group Pulse ─────────────────────────
interface Pulse {
  decisions: Array<{ id: string; category: string; title: string; reason: string; priority: string; dueAt: string | null; link: string }>;
  escalations: Array<{ id: string; title: string; reason: string; link: string; createdAt: string }>;
  atRisk: Array<{ id: string; ref: string; name: string; health: string; targetDate: string | null; overdue: number }>;
  breaches: Array<{ space: string; n: number }>;
  failedInspections: Array<{ id: string; ref: string; title: string; score: number; criticalFailed: number; submittedAt: string }>;
  stuckWorkflows: Array<{ id: string; ref: string; title: string }>;
  kpis: Array<{ code: string; name: string; open: number; overdue: number; done7d: number }>;
  slowApprovals: Array<{ id: string; ref: string; title: string; amount: string | null; createdAt: string }>;
}

export function PulsePage() {
  const q = useQuery({ queryKey: ["pulse"], queryFn: () => api<Pulse>("/api/pulse"), refetchInterval: 120_000 });
  if (!q.data) return q.isLoading ? <Empty>Loading…</Empty> : <ErrorNote error={q.error} />;
  const d = q.data;
  const quiet = !d.decisions.length && !d.escalations.length && !d.atRisk.length && !d.breaches.length && !d.failedInspections.length && !d.stuckWorkflows.length;
  const missed = d.breaches.reduce((n, b) => n + b.n, 0);
  return (
    <div className="space-y-5">
      <PageHeader title="Group Pulse" subtitle="Only what needs your attention: decisions, escalations, projects at risk and slow approvals." />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Stat label="Decisions for you" value={d.decisions.length} tone={d.decisions.length ? "brand" : "default"} />
        <Stat label="Escalations" value={d.escalations.length} tone={d.escalations.length ? "bad" : "default"} />
        <Stat label="Projects at risk" value={d.atRisk.length} tone={d.atRisk.length ? "warn" : "default"} />
        <Stat label="Missed SLAs" value={missed} tone={missed ? "bad" : "default"} />
        <Stat label="Critical inspection fails" value={d.failedInspections.length} tone={d.failedInspections.length ? "bad" : "default"} hint="Last 7 days" />
      </div>
      {quiet && (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm font-medium text-emerald-800">
          <CheckCircle2 size={18} className="text-emerald-600" /> All clear — nothing needs your attention right now.
        </div>
      )}
      <Card title="By company" icon={<Building2 size={16} />} flush bodyClassName="!pb-0">
        <div className="overflow-x-auto"><table className="table"><thead><tr><th className="!pl-5">Company</th><th className="!text-right">Open</th><th className="!text-right">Overdue</th><th className="!pr-5 !text-right">Done (7 days)</th></tr></thead>
          <tbody>{d.kpis.map((k) => <tr key={k.code}><td className="!pl-5 font-semibold">{k.name}</td><td className="text-right num">{k.open}</td><td className={clsx("text-right num", k.overdue > 0 && "font-bold text-red-600")}>{k.overdue}</td><td className="!pr-5 text-right num">{k.done7d}</td></tr>)}</tbody>
        </table></div>
      </Card>
      <div className="grid gap-5 lg:grid-cols-2">
        {d.decisions.length > 0 && <PulseList title="Decisions & approvals for you" icon={<Scale size={16} />} rows={d.decisions.map((x) => ({ key: x.id, to: x.link, title: x.title, sub: `${x.reason}${x.dueAt ? ` · due ${fmtDate(x.dueAt)}` : ""}` }))} />}
        {d.escalations.length > 0 && <PulseList title="Escalations" icon={<Siren size={16} className="text-red-500" />} rows={d.escalations.map((x) => ({ key: x.id, to: x.link, title: x.title, sub: x.reason }))} />}
        {d.atRisk.length > 0 && <PulseList title="Projects at risk" icon={<FolderKanban size={16} />} rows={d.atRisk.map((p) => ({ key: p.id, to: `/projects/${p.id}`, title: p.name, dot: p.health === "red" ? "bg-red-500" : p.health === "amber" ? "bg-amber-500" : "bg-emerald-500", sub: `${p.ref}${p.overdue ? ` · ${p.overdue} overdue` : ""}${p.targetDate ? ` · target ${p.targetDate}` : ""}` }))} />}
        {d.breaches.length > 0 && <PulseList title="Missed SLAs by area" icon={<AlarmClockOff size={16} />} rows={d.breaches.map((b) => ({ key: b.space, title: b.space, right: <span className="font-bold text-red-600 num">{b.n}</span> }))} />}
        {d.failedInspections.length > 0 && <PulseList title="Critical inspection failures" icon={<ClipboardX size={16} />} rows={d.failedInspections.map((r) => ({ key: r.id, to: `/inspections/${r.id}`, title: r.title, right: <span className="badge bg-red-50 text-red-700 ring-1 ring-inset ring-red-200">{r.criticalFailed} critical · {Number(r.score)}%</span> }))} />}
        {d.stuckWorkflows.length > 0 && <PulseList title="Workflows needing attention" icon={<GitBranch size={16} />} rows={d.stuckWorkflows.map((w) => ({ key: w.id, to: `/workflows/${w.id}`, title: w.title, sub: w.ref }))} />}
        {d.slowApprovals.length > 0 && <PulseList title="Approvals pending over 3 days" icon={<Hourglass size={16} />} rows={d.slowApprovals.map((a) => ({ key: a.id, to: `/approvals/${a.id}`, title: a.title, right: <span className="font-bold num">{eur(a.amount)}</span> }))} />}
      </div>
    </div>
  );
}

function PulseList({ title, icon, rows, limit = 6, empty }: { title: string; icon: React.ReactNode; rows: Array<{ key: string; to?: string; title: string; sub?: string; right?: React.ReactNode; dot?: string }>; limit?: number; empty?: string }) {
  const [all, setAll] = useState(false);
  if (!rows.length) return <Card title={title} icon={icon}><p className="muted text-sm">{empty ?? "Nothing here."}</p></Card>;
  const shown = all ? rows : rows.slice(0, limit);
  return (
    <Card title={title} icon={icon} count={rows.length} flush>
      <ul className="px-2">
        {shown.map((r) => {
          const inner = (
            <>
              {r.dot && <span className={clsx("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", r.dot)} />}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[var(--text)]">{r.title}</span>
                {r.sub && <span className="muted mt-0.5 block text-xs">{r.sub}</span>}
              </span>
              {r.right}
            </>
          );
          return (
            <li key={r.key}>
              {r.to ? <Link to={r.to as "/"} className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-[#f4f5fb]">{inner}</Link> : <div className="flex items-start gap-3 px-3 py-2.5">{inner}</div>}
            </li>
          );
        })}
      </ul>
      {rows.length > limit && <div className="px-5 pb-2"><button className="btn btn-ghost btn-sm -ml-2 text-brand-700" onClick={() => setAll((x) => !x)}>{all ? "Show fewer" : `Show all ${rows.length}`}</button></div>}
    </Card>
  );
}

// ───────────────────────── search palette ─────────────────────────
interface SearchGroups { groups: Array<{ type: string; filter?: string; label: string; items: Array<{ id: string; ref?: string | null; title: string; state?: string; link: string }> }> }

/** Where to look; ticked ones show (remembered in this browser). Counts show for all, so you see what you'd find. */
const WHERE = [["tasks", "Tasks"], ["notes", "Notes"], ["knowledge", "Knowledge"], ["documents", "Documents"], ["projects", "Projects"], ["mail", "Mail"], ["people", "People"], ["more", "More"]] as const;
const DEFAULT_WHERE = ["tasks", "notes", "knowledge", "documents"];
const loadWhere = (): string[] => { try { const v = JSON.parse(localStorage.getItem("one.search.where") ?? "null"); return Array.isArray(v) && v.length ? v : DEFAULT_WHERE; } catch { return DEFAULT_WHERE; } };

export function SearchBox() {
  const [q, setQ] = useState("");
  const [where, setWhere] = useState<string[]>(loadWhere);
  const flip = (k: string) => setWhere((cur) => {
    const next = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
    try { localStorage.setItem("one.search.where", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const res = useQuery({ queryKey: ["search", q], queryFn: () => api<SearchGroups>(`/api/search?q=${encodeURIComponent(q)}`), enabled: q.trim().length >= 2 });
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  return (
    <div className="relative w-full max-w-md">
      <Search size={16} className="muted absolute left-3 top-2.5" />
      <input ref={ref} className="input !pl-9" placeholder="Search everything  ⌘K" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} aria-label="Search" />
      {open && q.trim().length >= 2 && (
        <div className="panel absolute z-40 mt-1 max-h-[70vh] w-full overflow-auto p-1 shadow-xl">
          <div className="flex flex-wrap gap-1 border-b border-[var(--line)] px-1 pb-2 pt-1" onMouseDown={(e) => e.preventDefault() /* keep the box open */}>
            {WHERE.map(([k, label]) => {
              const n = (res.data?.groups ?? []).filter((g) => (g.filter ?? "more") === k).reduce((a, g) => a + g.items.length, 0);
              const on = where.includes(k);
              return (
                <button key={k} type="button" aria-pressed={on} onClick={() => flip(k)}
                  className={`chip cursor-pointer gap-1 !py-0.5 text-xs ${on ? "!border-brand-600 !bg-brand-50 text-brand-700" : ""}`}>
                  {on ? <CheckSquare size={12} /> : <Square size={12} />}{label}{res.data && <span className="opacity-70">{n}</span>}
                </button>
              );
            })}
          </div>
          {res.isLoading && <p className="muted p-2 text-sm">Searching…</p>}
          {!where.length && <p className="muted p-2 text-sm">Tick at least one place to search.</p>}
          {res.data && where.length > 0 && !res.data.groups.some((g) => where.includes(g.filter ?? "more")) && <p className="muted p-2 text-sm">Nothing you can see matches{res.data.groups.length ? " here — try ticking another place" : ""}.</p>}
          {res.data?.groups.filter((g) => where.includes(g.filter ?? "more")).map((g) => (
            <div key={g.type}>
              <div className="muted px-2 pt-2 text-xs font-semibold uppercase">{g.label}</div>
              {g.items.map((i) => (
                <button key={`${g.type}:${i.id}`} className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5" onMouseDown={() => { setOpen(false); setQ(""); if (i.link.startsWith("http")) window.open(i.link, "_blank"); else void navigate({ to: i.link as "/" }); }}>
                  {i.title} {i.ref && <span className="muted text-xs">{i.ref}</span>} {i.state && <span className="chip ml-1 !py-0 text-[10px]">{i.state.replace("_", " ")}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── AI ─────────────────────────
export function BriefingCard() {
  const m = useMutation({ mutationFn: () => post<{ briefing: string }>("/api/ai/briefing", {}) });
  return (
    <Card title={<span className="flex items-center gap-1"><Sparkles size={14} className="text-violet-500" /> Fusion AI briefing</span>} actions={<button className="btn !min-h-0 !py-1 text-xs" disabled={m.isPending} onClick={() => m.mutate()}>{m.isPending ? "Thinking…" : m.data ? "Refresh" : "Brief me"}</button>}>
      <ErrorNote error={m.error} />
      {m.data ? <div className="whitespace-pre-wrap text-sm">{m.data.briefing}</div> : <p className="muted text-sm">A short summary of what needs you today, from your own tasks and inbox only.</p>}
    </Card>
  );
}

export interface AiDraft { typeKey: string; title: string | null; data: Record<string, unknown>; confidence: number | null; why: string | null; note: string }
export function AiCreate({ onDraft }: { onDraft: (d: AiDraft) => void }) {
  const [text, setText] = useState("");
  const m = useMutation({ mutationFn: () => post<AiDraft>("/api/ai/draft-request", { text }), onSuccess: onDraft });
  return (
    <div className="rounded-lg border border-violet-300/60 p-3 dark:border-violet-800">
      <div className="mb-1 flex items-center gap-1 text-sm font-medium"><Sparkles size={14} className="text-violet-500" /> Describe what you need</div>
      <div className="flex gap-2">
        <input className="input" placeholder="e.g. Member Joe Borg's card is blocked at the gate" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim().length > 2 && m.mutate()} />
        <button className="btn" disabled={text.trim().length < 3 || m.isPending} onClick={() => m.mutate()}>{m.isPending ? "…" : "Go"}</button>
      </div>
      <ErrorNote error={m.error} />
      <p className="muted mt-1 text-xs">Fusion AI prepares the form; you check it and submit. It can't approve anything.</p>
    </div>
  );
}

export const AlertIcon = AlertTriangle;
